import React, { useEffect, useState } from 'react';
import { Menu, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { defaultKeySetter, items } from './sideBarArray';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../Context/AuthContext';
import UserRole from '../../Api/UserRole';
import { toastError } from '../../Components/Toast/ToastComponent';
import {
  getCheckPermission,
  getDashboard,
  setSettingsData,
} from '../../store/reducers/actionPermission';
import Settings from '../../Api/settingsApi';
import {
  removeAuthInfo,
  setSessionSettings,
} from '../../Context/AuthSessionStorage';

const SideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const data = useSelector(getDashboard);
  const { authState, logout } = useAuth();

  const [stateOpenKeys, setStateOpenKeys] = useState([]);
  const [menu, setMenu] = useState([]);
  const [selectedKey, setSelectedKey] = useState('/dashboard');

  useEffect(() => {
    if (location.pathname === '/') {
      const key = data?.checked ? '/dashboard' : '/timesheet';
      setSelectedKey(key);
      navigate(key, { replace: true });
    }
  }, [data?.checked, location.pathname, navigate]);

  useEffect(() => {
    if (!menu.length) return;

    // Returns true if `pathname` matches `key` exactly or as a strict prefix
    // e.g. key='/user' only matches '/user' or '/user/123', NOT '/user_timesheet'
    const pathMatchesKey = (pathname, key) => {
      if (!key || key === '/') return pathname === '/';
      if (pathname === key) return true;
      return pathname.startsWith(key + '/');
    };

    let parentKey = null;
    let childKey = null;

    // Also handle /my_user_timesheet -> highlight /user_timesheet
    const ROUTE_MAP = {
      '/my_user_timesheet': '/user_timesheet',
    };
    const mappedPath = Object.keys(ROUTE_MAP).find((prefix) =>
      location.pathname.startsWith(prefix),
    );
    const effectivePath = mappedPath
      ? ROUTE_MAP[mappedPath]
      : location.pathname;

    menu.forEach((item) => {
      if (item.children) {
        item.children.forEach((child) => {
          if (child.key && pathMatchesKey(effectivePath, child.key)) {
            parentKey = item.key;
            childKey = child.key;
          }
        });
      } else {
        if (item.key && pathMatchesKey(effectivePath, item.key)) {
          childKey = item.key;
        }
      }
    });

    if (childKey) setSelectedKey(childKey);
    if (parentKey) setStateOpenKeys([parentKey]);
  }, [location.pathname, menu]);

  useEffect(() => {
    getMenu();
    getSettings();
  }, [authState]);

  const getSettings = async () => {
    try {
      const response = await Settings.get();
      if (response.data.success) {
        setSessionSettings(JSON.stringify(response.data.result[0]));
        dispatch(setSettingsData(response.data.result[0]));
      } else {
        toastError(response?.data?.message);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const getLevelKeys = (items1) => {
    const key = {};
    const func = (items2, level = 1) => {
      items2.forEach((item) => {
        if (item.key) key[item.key] = level;
        if (item.children) func(item.children, level + 1);
      });
    };
    func(items1);
    return key;
  };

  const levelKeys = getLevelKeys(items);

  const onOpenChange = (openKeys) => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1,
    );

    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          .filter((_, index) => index !== repeatIndex)
          .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      setStateOpenKeys(openKeys);
    }
  };

  const getMenu = async () => {
    try {
      const response = await UserRole.getMenuPermission({
        id: authState?.userRoleID,
      });

      if (response.data.success) {
        const menuCheck = JSON.parse(response.data.result.user_permissions);

        const filteredArray = items.filter((item) => {
          const checkItem = menuCheck.find(
            (checkItem) => checkItem.statename === item.statename,
          );

          if (checkItem && checkItem.checked) {
            if (item.children) {
              item.children = item.children.filter((child) => {
                const childCheckItem = checkItem.children.find(
                  (checkChild) => checkChild.statename === child.statename,
                );
                return childCheckItem && childCheckItem.checked;
              });
            }
            return true;
          }
          return false;
        });

        dispatch(getCheckPermission(menuCheck));
        setMenu(filteredArray);
      } else {
        removeAuthInfo();
        logout();
        toastError(response.data.message);
      }
    } catch (error) {
      removeAuthInfo();
      logout();
      toastError(error);
    }
  };

  const handleMenuClick = (item) => {
    setSelectedKey(item.key);
    navigate(item.key);
  };

  return (
    <>
      {menu.length === 0 ? (
        <div className="text-center">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      ) : (
        <Menu
          theme="dark"
          mode="inline"
          openKeys={stateOpenKeys}
          onOpenChange={onOpenChange}
          onClick={handleMenuClick}
          items={menu}
          selectedKeys={[selectedKey]}
          style={{ fontSize: 16 }}
        />
      )}
    </>
  );
};

export default SideBar;
